import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { TrashAltIcon } from 'assets/icons';
import { Dialog, useDisclosure } from 'components/Dialog';
import {
  addEmptyOrderListRow,
  addReferenceListToCartRequest,
  getCreateOrderList,
  getCreateOrderListErrorRows,
  resetOrderListReferences,
  setOrderListFileReferencesIsModalOpen,
} from 'domains/orderList/OrderList.store';
import { CreateOrderList } from 'domains/orderList/OrderList.types';
import { getUserCommercialLink } from 'domains/user';
import { FileReferencesRowsErrors } from 'pages/CartPage/CartStep/FileUpload';
import OrderListTable, { getListRefOccurrences } from 'pages/OrderListPage/OrderListTable';
import {
  Box,
  CenteredSpin,
  ErrorButton,
  Flex,
  MarginBox,
  notifyTop,
  PlusCircleButton,
  Text,
  WhiteButton,
  YellowButton,
} from 'UI';
import { LOADING, SEARCH_STATUS, useLarge } from 'utils';
import { useResetScroll } from 'utils/hooks/useResetScroll';
import OrderListReferencesFileUpload from './OrderListFileUpload/OrderListReferencesFileUpload';

function isOrderListValid(orderList: CreateOrderList, hasUnknownReference: boolean) {
  const isAnyReferenceFilled = orderList.references.some((ref) => ref.referenceNumber);
  return (
    isAnyReferenceFilled &&
    !hasUnknownReference &&
    !(
      orderList.references.length > 0 &&
      !!orderList.references
        .filter((row) => row.referenceNumber)
        .find((leftRow) => {
          return !leftRow.quantity || getListRefOccurrences(orderList, leftRow.referenceNumber) !== 1;
        })
    )
  );
}

const AddReferenceListToCartButton = ({
  searchStatus,
  hasUnknownReference,
}: {
  searchStatus: SEARCH_STATUS;
  hasUnknownReference: boolean;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const orderList = useSelector(getCreateOrderList);

  const isListValid = useMemo(() => {
    return isOrderListValid(orderList, hasUnknownReference);
  }, [orderList, hasUnknownReference]);

  const handleAddToCard = () => {
    dispatch(addReferenceListToCartRequest());
  };

  return (
    <>
      {searchStatus === LOADING ? (
        <Box height={40}>
          <CenteredSpin size={'small'} />
        </Box>
      ) : (
        <YellowButton onClick={handleAddToCard} disabled={!isListValid}>
          <Text type={'h4_paragraph'}>{t('catalog.order_list.add_to_my_cart', 'Add to my cart')}</Text>
        </YellowButton>
      )}
    </>
  );
};

const UploadReferencesFromCSVButton = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const commercialLink = useSelector(getUserCommercialLink);

  function openModal() {
    if (commercialLink === null) {
      notifyTop('error', t('cart.file_upload.seller_is_missing', 'Seller is missing, contact your R1.'), null);
    } else {
      dispatch(setOrderListFileReferencesIsModalOpen(true));
    }
  }

  return (
    <WhiteButton onClick={openModal}>
      <Text type={'h4_paragraph'}>{t('catalog.order_list.upload_your_csv', 'Upload your CSV')}</Text>
    </WhiteButton>
  );
};

const splitOrderList = (orderList: CreateOrderList) => {
  return {
    referencesLeftColumn: [...orderList.references].filter((_v, i) => !(i % 2)),
    referencesRightColumn: [...orderList.references].filter((_v, i) => i % 2),
  };
};

const CreateOrderListPage = () => {
  const { t } = useTranslation();
  const orderList = useSelector(getCreateOrderList);
  const dispatch = useDispatch();
  const large = useLarge();
  const addToCartResponseStatus = useSelector(getCreateOrderListErrorRows);
  const [unknownReferences, setUnknownReferences] = useState<number[]>([]);
  const { referencesLeftColumn, referencesRightColumn } = splitOrderList(orderList);
  useResetScroll();

  const disclosure = useDisclosure();
  const { onOpen } = disclosure;

  const addRow = () => {
    dispatch(addEmptyOrderListRow());
  };

  const handleUnknownReferenceChange = (referenceIndex: number, action: 'add' | 'remove') => {
    if (action === 'add') {
      if (unknownReferences.find((idx) => idx === referenceIndex) === undefined) {
        setUnknownReferences([...unknownReferences, referenceIndex]);
      }
    } else {
      setUnknownReferences(unknownReferences.filter((ref) => ref !== referenceIndex));
    }
  };

  const handleResetReferenceList = () => {
    setUnknownReferences([]);
    dispatch(resetOrderListReferences());
  };

  return (
    <Flex direction={'column'}>
      <Flex>
        <Flex direction={'column'} justify={'space-between'} size={1}>
          <Text type={'h4_paragraph_title'}>{t('catalog.order_list.create_order_list', 'Create order list')}</Text>
          {/* Hide undeveloped features for Anthony's Request*/}
          {/*<MarginBox mt={60} />*/}
          {/*<Box height={40} width={390}>*/}
          {/*  <Input*/}
          {/*    value={orderList.name}*/}
          {/*    onChange={(name) => dispatch(setOrderListName(name))}*/}
          {/*    placeholder={t('catalog.order_list.order_list_name', 'Order list name')}*/}
          {/*    bordered*/}
          {/*    maxLength={63}*/}
          {/*  />*/}
          {/*</Box>*/}
          <Box>
            <ErrorButton onClick={onOpen}>
              <Flex gap={10}>
                <TrashAltIcon width={18} height={18} />
                <Text type={'light_14'}>{t('catalog.order_list.delete_all_items', 'Delete all items')}</Text>
              </Flex>
            </ErrorButton>
          </Box>
        </Flex>
        <Flex direction={'column'} justify={'flex-end'} size={0}>
          <Flex direction={'column'} minWidth={240}>
            <AddReferenceListToCartButton
              searchStatus={addToCartResponseStatus?.searchStatus}
              hasUnknownReference={unknownReferences.length > 0}
            />
            <MarginBox mt={30} />
            <UploadReferencesFromCSVButton />
          </Flex>
        </Flex>
      </Flex>
      <Flex direction={'column'} justify={'center'}>
        <FileReferencesRowsErrors errorRows={addToCartResponseStatus?.data ?? []} />
        <OrderListReferencesFileUpload />
        <MarginBox mt={30} />
        {large ? (
          <Flex direction={'row'}>
            <OrderListTable
              references={referencesLeftColumn}
              handleUnknownReferenceChange={handleUnknownReferenceChange}
            />
            <MarginBox mr={30} />
            <OrderListTable
              references={referencesRightColumn}
              handleUnknownReferenceChange={handleUnknownReferenceChange}
            />
          </Flex>
        ) : (
          <OrderListTable
            references={orderList.references}
            handleUnknownReferenceChange={handleUnknownReferenceChange}
          />
        )}
      </Flex>
      <MarginBox mt={30} />
      <PlusCircleButton onClick={addRow} data-cy={'add-new-reference-button'}>
        <Text type="base" bold color="clear_blue">
          {t('catalog.order_list.add_new_reference', 'Add new reference')}
        </Text>
      </PlusCircleButton>
      <MarginBox mt={30} />
      <Dialog
        disclosure={disclosure}
        title={t('catalog.order_list.action.delete_all_items', 'Delete all items')}
        description={[
          t(
            'catalog.order_list.action.delete_all_items.description',
            'This action will delete all the references from your order.',
          ),
          t('common.dialog.confirmationQuestion', 'Would you like to confirm?'),
        ]}
        icon={TrashAltIcon}
        status={'error'}
        handleConfirm={handleResetReferenceList}
      />
    </Flex>
  );
};

export default CreateOrderListPage;
